import type { SPIntl } from 'contexts/intl/intlContext';
import type { IntlKey } from 'locales';
import type { QuoteIndexableData } from 'state/quotes/dashboard/selectors';
import type { SolvingModeDetail } from 'state/quotes/details/details.model';
import { camelCaseToWords } from 'utils/string';

export function solveModeLabel({ label, id }: SolvingModeDetail, formatMessage: SPIntl['formatMessage']): string {
  return label
    ? formatMessage({ id: `field.${label}` as any })
    : formatMessage({
        id: `field.dynamic.${id}` as any,
        defaultMessage: camelCaseToWords(id),
      });
}

export function quickSearchFiltering(
  quote: QuoteIndexableData,
  formatMessage: SPIntl['formatMessage'],
  formatDisplayedDate: SPIntl['formatDisplayedDate'],
  quickSearchTerm?: string,
): boolean {
  if (!quickSearchTerm) {
    return true;
  }

  const indexableFields = [
    quote?.clientName,
    quote?.clientId,
    quote.quoteExternalId,
    quote.subProduct && formatSubProduct(quote.subProduct, formatMessage),
    quote.wrapper && formatWrapper(quote.wrapper, formatMessage),
    quote.solveMode && solveModeLabel(quote.solveMode, formatMessage),
    quote.maturityDate && formatMaturityDate(quote.maturityDate, formatDisplayedDate),
    `${quote.tenor}` + (quote.tenorUnit && ' ' + formatTenorUnit(quote.tenorUnit, formatMessage, quote.tenor)),
    quote.currency,
    ...quote.underlyings,
  ].filter(field => !!field);

  const index = indexableFields.join('').toLowerCase();

  return index.includes(quickSearchTerm.toLowerCase());
}

export const UnknowClientId = 'UNKNOWN';

export function formatSubProduct(subProduct: string, formatMessage: SPIntl['formatMessage']): string {
  return formatMessage({ id: `field.${subProduct}` as any, defaultMessage: camelCaseToWords(subProduct) });
}

export function formatWrapper(wrapper: string, formatMessage: SPIntl['formatMessage']): string {
  return formatMessage({ id: `form.Wrapper.${wrapper}` as any });
}

export function formatMaturityDate(maturityDate: string, formatDisplayedDate: SPIntl['formatDisplayedDate']): string {
  return formatDisplayedDate(maturityDate);
}

export function formatTenorUnit(tenorUnit: string, formatMessage: SPIntl['formatMessage'], count: number): string {
  const translationKey = getTenorUnitTranslationKey(tenorUnit);
  return formatMessage({ id: translationKey, defaultMessage: tenorUnit }, { count });
}

function getTenorUnitTranslationKey(tenorUnit: string): IntlKey {
  switch (tenorUnit) {
    case 'Day':
      return 'dashboard.tenor.Day';
    case 'Week':
      return 'dashboard.tenor.Week';
    case 'Month':
      return 'dashboard.tenor.Month';
    case 'Year':
      return 'dashboard.tenor.Year';
    case 'custom':
      return 'dashboard.tenor.custom';
    default:
      return tenorUnit as never;
  }
}
